/**
 * canvasTools — shared tool registry for voice + chat agents.
 *
 * Architecture:
 *  - One source of truth for "things an agent can do on the canvas."
 *  - Each tool has a JSON-schema declaration (consumed by Gemini Live and
 *    Gemini text function-calling) AND a runtime handler that mutates the
 *    canvas state (panels, selection, filters, artifacts).
 *  - Handlers operate on `useLayoutStore.getState()` (imperative) so they
 *    work outside React render cycles (Gemini Live message handler runs in
 *    a WebSocket callback, not a React effect).
 *  - Cross-panel intents (e.g. "open positions panel filtered to IT") flow
 *    via a small zustand `panelIntentStore` so panels can subscribe without
 *    coupling to voice/chat.
 *  - Window CustomEvent dispatch is exposed too for non-React consumers
 *    (Lit components, embedded scripts).
 *
 * Why not CopilotKit:
 *  - We want one tool surface that works in BOTH the voice model (Gemini
 *    Live, has its own function-calling protocol) and a chat model running
 *    against Gemini text. CopilotKit's `useFrontendTool` only registers
 *    tools with its own runtime; the voice path bypasses it entirely.
 *  - When the platform wants to bridge to CopilotKit later, this registry
 *    becomes the input to a CopilotKit adapter. Keep the data shape
 *    library-agnostic.
 */
import { useLayoutStore } from '../../stores/layoutStore';
import { usePanelIntentStore } from '../../stores/panelIntentStore';
import { getJobsCatalog } from '../../stores/jobsCatalogStore';
import { getRoleTaxonomyIndex } from '../../stores/roleTaxonomyStore';
import {
  filterJobs,
  findJobByTitle,
  findJobTitleMatches,
  formatJobsToolResult,
  buildDepartmentLexicon,
  buildTrackLexicon,
  normalizeJobFilterArgs,
} from '../jobPanelUtils';
import { resolveRoleIntentFromArgs } from '../roleTaxonomy';
import type { PanelId } from '../../types';
import {
  closePanelInCanvas,
  getEditor as getWhiteboardEditor,
  openPanelInCanvas,
} from '../../whiteboard/shapes/panelShapeApi';

/**
 * JSON-schema-style declaration. Matches the shape Gemini Live expects in
 * `config.tools[].functionDeclarations[]` (a subset of OpenAPI 3.0).
 *
 * Keep `parameters.type` as `'object'` — Gemini Live rejects top-level
 * schemas of any other type.
 */
export interface ToolDeclaration {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, ToolParameterSchema>;
    required?: string[];
  };
}

export interface ToolParameterSchema {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  enum?: readonly string[];
  items?: ToolParameterSchema;
}

/**
 * Result of a tool execution. Strings are returned directly to the agent
 * as `functionResponse.response.result`. Errors are surfaced as
 * `{ error: string }` so the agent can recover ("I tried that but…")
 * instead of silently dropping the call.
 */
export type ToolResult =
  | { ok: true; result: unknown }
  | { ok: false; error: string };

export type ToolHandler = (args: Record<string, unknown>) => ToolResult | Promise<ToolResult>;

export interface ToolDefinition {
  declaration: ToolDeclaration;
  handler: ToolHandler;
}

const KNOWN_PANELS: readonly PanelId[] = [
  'nav', 'chat', 'artifacts',
  'open-positions', 'applications', 'resources',
  'career-tools', 'growth-paths', 'career-trajectories',
  'voice', 'journey', 'settings',
];

/**
 * Show a panel on whichever substrate is currently mounted.
 *
 * The prototype runs two canvas substrates in parallel routes:
 *  • `/career-canvas`            → legacy absolute-positioned canvas
 *  • `/career-canvas-whiteboard` → tldraw whiteboard
 *
 * They share the AI/data layer (this file), the panelIntentStore, the
 * voiceKernel, etc. — but disagree on layout. Since only one is mounted
 * per tab, we always update `useLayoutStore` (cheap, in-memory zustand
 * write — no-op cost when no panels are listening) AND additionally call
 * `openPanelInCanvas` if a whiteboard editor is bound.
 *
 * `panelProps` is the shape-scoped payload forwarded into the whiteboard
 * panel component. Cross-cut intents (selectedJobId, search, department)
 * still flow through `panelIntentStore` so both substrates see them; the
 * `panelProps` is for shape-local data (e.g. a friendly title to render
 * in the chrome).
 */
function showPanel(
  id: PanelId,
  source: string,
  panelProps?: Record<string, unknown>,
): ToolResult {
  if (!KNOWN_PANELS.includes(id)) {
    return { ok: false, error: `unknown panel id "${id}"` };
  }
  useLayoutStore.getState().showPanel(id, 'tool');
  // Forward to the whiteboard ONLY if its editor is bound — never queue,
  // because the legacy canvas's tool calls shouldn't pile up in the
  // whiteboard's pending queue. The voice path's pre-mount race is
  // handled by `openPanelInCanvas` itself when called directly from voice
  // bootstrap, not via this funnel.
  if (getWhiteboardEditor()) {
    openPanelInCanvas(id, { panelProps, focus: true });
  }
  return {
    ok: true,
    result: `Opened ${id} panel${source ? ` (via ${source})` : ''}.`,
  };
}

function hidePanel(id: PanelId): ToolResult {
  if (!KNOWN_PANELS.includes(id)) {
    return { ok: false, error: `unknown panel id "${id}"` };
  }
  useLayoutStore.getState().hidePanel(id);
  if (getWhiteboardEditor()) {
    closePanelInCanvas(id);
  }
  return { ok: true, result: `Closed ${id} panel.` };
}

/**
 * Tool definitions. The list and order are stable — the voice model is
 * told about every tool at session start, and adding/removing from the
 * middle of the array would invalidate any cached function-call ids.
 *
 * Adding a new tool: append to the array. Don't reshuffle.
 */
export const CANVAS_TOOLS: readonly ToolDefinition[] = [
  {
    declaration: {
      name: 'open_positions',
      description:
        'Open the Open Positions panel filtered to matching moss.com roles. ALWAYS call this when the candidate asks about jobs, openings, pay, locations, departments, or role types — do not only describe roles in speech. Prefer **roleIntent** for conversational role types (project_management, field_leadership, solar_field_hourly, safety, engineering, corporate_support). Use **location** for geographic area (Texas, DFW, South Florida, Colorado, Hawaii) — NOT search. Use department only for a single exact moss.com job family. Use track for Professionals vs Solar Hourly. Use search only for title/skill keywords (BESS, $20/hr) — not cities/states or broad role types.',
      parameters: {
        type: 'object',
        properties: {
          roleIntent: {
            type: 'string',
            description:
              'PREFERRED for role types spanning multiple departments. Examples: project_management (PM, project manager), field_leadership (superintendent), solar_field_hourly (laborer, operator, CDL), safety, engineering, preconstruction_estimating, solar_qc, planning_scheduling, commissioning, corporate_support, executive. Synonyms work: PM, superintendent, laborer, QC, HR.',
          },
          department: {
            type: 'string',
            description:
              'Optional single moss.com job-family filter when you need one exact department (not a role type). Examples: CM Project Management, CM Field Operations, Solar Field Operations. Prefer roleIntent for PM, superintendent, solar hourly, etc.',
          },
          track: {
            type: 'string',
            description:
              'Optional career track: "Professionals (Salaried)" for salaried CM/corporate roles, or "Solar Hourly" for field hourly solar roles. Synonyms: salaried, professional, hourly, field, solar hourly.',
          },
          location: {
            type: 'string',
            description:
              'Geographic area filter. Use for ANY location question: "Texas", "DFW", "Dallas", "South Florida", "Fort Lauderdale", "Colorado", "New Mexico", "Hawaii", "remote". Matches cities in job titles and multi-state postings. Do NOT put area names in search — use this parameter.',
          },
          search: {
            type: 'string',
            description:
              'Title/skill keywords ONLY — not geography or broad role types (use roleIntent). Examples: "BESS", "$20", "intern". For cities/states use location; for PM/superintendent/laborer use roleIntent.',
          },
        },
      },
    },
    handler: ({ roleIntent, department, search, track, location }) => {
      const jobs = getJobsCatalog();
      const roleIndex = getRoleTaxonomyIndex();
      const deptLex = jobs.length > 0 ? buildDepartmentLexicon(jobs.map((j) => j.department)) : undefined;
      const trackLex = jobs.length > 0 ? buildTrackLexicon(jobs.map((j) => j.track ?? '')) : undefined;

      const roleIntentArg = typeof roleIntent === 'string' ? roleIntent : undefined;
      const deptArg = typeof department === 'string' ? department : undefined;
      const trackArg = typeof track === 'string' ? track : undefined;
      const locationArg = typeof location === 'string' ? location : undefined;
      const searchArg = typeof search === 'string' ? search : undefined;

      const roleResolved = resolveRoleIntentFromArgs(
        { roleIntent: roleIntentArg, department: deptArg, search: searchArg },
        roleIndex,
      );

      const normalizedDept =
        roleResolved.department && deptLex
          ? deptLex.normalize(roleResolved.department)
          : null;
      const normalizedTrack = trackArg && trackLex ? trackLex.normalize(trackArg) : null;

      const filterArgs = normalizeJobFilterArgs(
        {
          roleIntent: roleResolved.roleIntent ?? roleIntentArg,
          department: normalizedDept ?? roleResolved.department,
          track: normalizedTrack ?? trackArg,
          location: locationArg,
          search: roleResolved.search,
        },
        roleIndex,
      );

      usePanelIntentStore.getState().setOpenPositionsIntent({
        roleIntent: filterArgs.roleIntent,
        department: filterArgs.department,
        track: filterArgs.track,
        location: filterArgs.location,
        search: filterArgs.search,
        selectedJobId: null,
      });

      const filtered = filterJobs(
        jobs,
        filterArgs,
        deptLex,
        trackLex,
        roleIndex,
      );

      const panelResult = showPanel('open-positions', 'tool');
      if (!panelResult.ok) return panelResult;

      return {
        ok: true,
        result: formatJobsToolResult(filtered, filterArgs),
      };
    },
  },
  {
    declaration: {
      name: 'show_job_detail',
      description:
        'Open a specific job detail view in the Open Positions panel. Use when discussing one role by name. Prefer jobTitle with the moss.com posting title (partial match OK). Use jobId when you know the numeric id from a prior open_positions result.',
      parameters: {
        type: 'object',
        properties: {
          jobId: {
            type: 'number',
            description: 'Numeric id from open_positions preview (e.g. 42).',
          },
          jobTitle: {
            type: 'string',
            description:
              'Role title partial match — e.g. "Solar Laborer", "Safety Manager", "Project Manager".',
          },
        },
      },
    },
    handler: ({ jobId, jobTitle }) => {
      const jobs = getJobsCatalog();
      let resolvedId: number | null = typeof jobId === 'number' ? jobId : null;
      let resolvedTitle: string | undefined;

      if (typeof jobTitle === 'string' && jobTitle.trim()) {
        const match = findJobByTitle(jobs, jobTitle.trim());
        if (match) {
          resolvedId = match.id;
          resolvedTitle = match.title;
        }
      }

      usePanelIntentStore.getState().setOpenPositionsIntent({
        selectedJobId: resolvedId,
        selectedJobTitle: resolvedTitle ?? (typeof jobTitle === 'string' ? jobTitle : undefined),
      });

      const panelResult = showPanel('open-positions', 'tool');
      if (!panelResult.ok) return panelResult;

      if (resolvedId !== null && resolvedTitle) {
        return { ok: true, result: `Opened detail for #${resolvedId} ${resolvedTitle}.` };
      }
      if (typeof jobTitle === 'string' && jobTitle.trim()) {
        const alternatives = findJobTitleMatches(jobs, jobTitle.trim(), 4);
        if (alternatives.length > 0) {
          const list = alternatives.map((j) => `#${j.id} ${j.title}`).join('; ');
          return {
            ok: true,
            result: `Opened Open Positions — no exact match for "${jobTitle}". Close matches: ${list}. Ask the candidate which one, then call show_job_detail with jobId.`,
          };
        }
        return {
          ok: true,
          result: `Opened Open Positions — no match for "${jobTitle}". Try open_positions with a broader search.`,
        };
      }
      return panelResult;
    },
  },
  {
    declaration: {
      name: 'open_learning',
      description:
        "Open the company learning view — the Resources panel filtered to learning content. Use when the candidate asks about training, certifications, or career growth.",
      parameters: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Optional search term (e.g. "leadership", "onboarding").',
          },
        },
      },
    },
    handler: ({ search }) => {
      usePanelIntentStore.getState().setResourcesIntent({
        search: typeof search === 'string' ? search : undefined,
        learningOnly: true,
      });
      return showPanel('resources', 'tool');
    },
  },
  {
    declaration: {
      name: 'open_growth_paths',
      description:
        "Open the Growth Paths panel showing career trajectory examples. Use when the candidate asks 'where could this role lead?' or wants to see example career trajectories.",
      parameters: {
        type: 'object',
        properties: {
          fromRole: {
            type: 'string',
            description: 'Optional starting role to highlight (e.g. "Engineer I", "Customer Success Specialist").',
          },
        },
      },
    },
    handler: ({ fromRole }) => {
      usePanelIntentStore.getState().setGrowthPathsIntent({
        fromRole: typeof fromRole === 'string' ? fromRole : undefined,
      });
      return showPanel('growth-paths', 'tool');
    },
  },
  {
    declaration: {
      name: 'open_resources',
      description:
        'Open the Resources panel — guides, videos, benefits, handbooks. General-purpose; use open_learning specifically for learning/training content.',
      parameters: {
        type: 'object',
        properties: {
          search: {
            type: 'string',
            description: 'Optional search term to filter resources.',
          },
        },
      },
    },
    handler: ({ search }) => {
      usePanelIntentStore.getState().setResourcesIntent({
        search: typeof search === 'string' ? search : undefined,
        learningOnly: false,
      });
      return showPanel('resources', 'tool');
    },
  },
  {
    declaration: {
      name: 'open_career_tools',
      description:
        'Open the Career Tools panel (resume builder, interview prep, etc).',
      parameters: { type: 'object', properties: {} },
    },
    handler: () => showPanel('career-tools', 'tool'),
  },
  {
    declaration: {
      name: 'open_applications',
      description:
        "Open the candidate's My Applications panel showing roles they've already applied to.",
      parameters: { type: 'object', properties: {} },
    },
    handler: () => showPanel('applications', 'tool'),
  },
  {
    declaration: {
      name: 'open_journey',
      description:
        "Open the Journey panel showing the candidate's progress / timeline on the canvas.",
      parameters: { type: 'object', properties: {} },
    },
    handler: () => showPanel('journey', 'tool'),
  },
  {
    declaration: {
      name: 'open_chat',
      description:
        'Open the chat panel and focus the input. Use when handing off from voice to text.',
      parameters: { type: 'object', properties: {} },
    },
    handler: () => {
      const result = showPanel('chat', 'tool');
      window.dispatchEvent(new CustomEvent('landi:focus-chat-input'));
      return result;
    },
  },
  {
    declaration: {
      name: 'dismiss_panel',
      description: 'Close a panel by id.',
      parameters: {
        type: 'object',
        properties: {
          panelId: {
            type: 'string',
            description:
              'Panel id to close. Valid values: open-positions, applications, resources, career-tools, growth-paths, career-trajectories, journey, artifacts, chat, voice, settings.',
          },
        },
        required: ['panelId'],
      },
    },
    handler: ({ panelId }) => {
      if (typeof panelId !== 'string') {
        return { ok: false, error: 'panelId must be a string' };
      }
      return hidePanel(panelId as PanelId);
    },
  },
  {
    declaration: {
      name: 'save_job',
      description: 'Bookmark / save a job for the candidate. Job must be open in the Positions panel first.',
      parameters: {
        type: 'object',
        properties: {
          jobId: { type: 'number', description: 'Numeric job id to save.' },
        },
        required: ['jobId'],
      },
    },
    handler: ({ jobId }) => {
      if (typeof jobId !== 'number') {
        return { ok: false, error: 'jobId must be a number' };
      }
      usePanelIntentStore.getState().toggleSavedJob(jobId);
      return { ok: true, result: `Saved job ${jobId}.` };
    },
  },
  {
    declaration: {
      name: 'share_artifact',
      description:
        'Add a generated artifact (resume snippet, summary, plan) to the Artifacts panel for the candidate to review later.',
      parameters: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'Short label for the artifact.' },
          content: { type: 'string', description: 'The artifact body — markdown or plain text.' },
          kind: {
            type: 'string',
            description: 'Optional artifact type label (e.g. "summary", "resume", "plan").',
          },
        },
        required: ['name', 'content'],
      },
    },
    handler: ({ name, content, kind }) => {
      if (typeof name !== 'string' || typeof content !== 'string') {
        return { ok: false, error: 'name and content must be strings' };
      }
      usePanelIntentStore.getState().pushArtifact({
        id: `art-${Date.now().toString(36)}`,
        name,
        content,
        kind: typeof kind === 'string' ? kind : 'note',
        createdAt: new Date().toISOString(),
      });
      useLayoutStore.getState().showPanel('artifacts');
      return { ok: true, result: `Added "${name}" to artifacts.` };
    },
  },
  {
    declaration: {
      name: 'host_action',
      description:
        'Trigger an action in the host application. Use this whenever the user asks to navigate to a screen, change app state, open a flow, set a filter, or run a workflow. The host app maps `action` to its own router/state. Always include the smallest set of `params` the action needs.',
      parameters: {
        type: 'object',
        properties: {
          action: {
            type: 'string',
            description:
              'Action id understood by the host app (e.g. "navigate", "open_create", "open_history", "open_settings", "open_template", "post_now", "schedule_post"). Tenant-specific — see the host system prompt for the valid set.',
          },
          params: {
            type: 'object',
            description:
              'Optional parameters for the action. Examples: { route: "/(tabs)/history" } for navigate, { filter: "scheduled" } for open_history, { templateId: "tpl_42" } for open_template.',
          },
        },
        required: ['action'],
      },
    },
    handler: ({ action, params }) => {
      if (typeof action !== 'string' || action.trim().length === 0) {
        return { ok: false, error: 'action must be a non-empty string' };
      }
      const safeParams: Record<string, unknown> =
        params && typeof params === 'object' && !Array.isArray(params)
          ? (params as Record<string, unknown>)
          : {};
      // Bridge to the host: a CustomEvent on `window` is the lowest-common-
      // denominator that works for any host (RN WebView postMessage relay,
      // plain web app event listener, Lit element listener).
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('agent:host-action', {
            detail: { action, params: safeParams },
          }),
        );
      }
      return { ok: true, result: `Triggered ${action} in host app.` };
    },
  },
  {
    declaration: {
      name: 'knowledge_search',
      description:
        'Search the tenant knowledge base (vetted internal docs, brand voice, policy text). Returns grounding chunks you can cite by name. Use for facts you are not certain about.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Free-text query, 4-200 chars.' },
          topK: { type: 'number', description: 'How many chunks to return (1-10). Default 6.' },
        },
        required: ['query'],
      },
    },
    handler: async ({ query, topK }) => {
      if (typeof query !== 'string' || query.trim().length < 3) {
        return { ok: false, error: 'query must be a string of at least 3 chars' };
      }
      const k = Math.min(10, Math.max(1, typeof topK === 'number' ? topK : 6));
      const endpoint = (import.meta.env.VITE_KNOWLEDGE_SEARCH_URL as string | undefined)?.trim();
      if (!endpoint) {
        return {
          ok: false,
          error:
            'knowledge_search not configured (set VITE_KNOWLEDGE_SEARCH_URL). Answer from system-prompt facts only.',
        };
      }
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ q: query.trim(), topK: k }),
        });
        if (!response.ok) {
          return { ok: false, error: `knowledge_search HTTP ${response.status}` };
        }
        const data = await response.json();
        return { ok: true, result: data };
      } catch (err) {
        return { ok: false, error: `knowledge_search failed: ${(err as Error).message}` };
      }
    },
  },
] as const;

/**
 * Per-tenant tool allow-lists. When a tenant id appears in this map, ONLY
 * the listed tool names are exposed to the agent for that tenant. Tenants
 * not in this map see the full registry (back-compat with the original
 * career-canvas / `default` deployment).
 *
 * Why allow-list over deny-list: when a host embeds the canvas as voice-only
 * for a non-career use case (e.g. WAVE Content Studio for travel agents),
 * leaking `open_positions` etc. into the model's tool surface invites
 * hallucinated panel calls that 404 in the host. The allow-list is opt-in
 * per tenant so the default deployment is unchanged.
 */
const TENANT_TOOL_ALLOWLIST: Readonly<Record<string, ReadonlySet<string>>> = {
  wave: new Set(['host_action']),
};

function filterToolsForTenant(
  tools: readonly ToolDefinition[],
  tenant?: string,
): readonly ToolDefinition[] {
  if (!tenant) return tools;
  const allow = TENANT_TOOL_ALLOWLIST[tenant];
  if (!allow) return tools;
  return tools.filter((t) => allow.has(t.declaration.name));
}

/**
 * Lookup helper. Tools are flat (no namespaces yet) so the registry is
 * keyed on `declaration.name` directly. Tenant-aware so a tenant cannot
 * call a tool that is not on its allow-list.
 */
export function getTool(name: string, tenant?: string): ToolDefinition | undefined {
  return filterToolsForTenant(CANVAS_TOOLS, tenant).find(
    (t) => t.declaration.name === name,
  );
}

/**
 * Execute a tool by name. Surfaces unknown-tool as a structured error so
 * the agent gets a textual failure (and can apologise / retry) instead of
 * a silent drop. When `tenant` is provided, the tool must be on that
 * tenant's allow-list.
 */
export async function executeTool(
  name: string,
  args: Record<string, unknown>,
  tenant?: string,
): Promise<ToolResult> {
  const tool = getTool(name, tenant);
  if (!tool) {
    return { ok: false, error: `unknown tool "${name}"` };
  }
  try {
    return await tool.handler(args ?? {});
  } catch (err) {
    return { ok: false, error: `${name} threw: ${(err as Error).message}` };
  }
}

/**
 * Function-declaration array suitable for Gemini Live `config.tools[0].functionDeclarations`
 * AND Gemini text `tools[0].functionDeclarations`. When `tenant` is provided
 * and the tenant has an allow-list, only allow-listed declarations are returned.
 */
export function getFunctionDeclarations(tenant?: string): ToolDeclaration[] {
  return filterToolsForTenant(CANVAS_TOOLS, tenant).map((t) => t.declaration);
}
